import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Badge,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  Delete,
  Visibility,
  Edit,
  Campaign,
  CheckCircle,
  Cancel,
  LocalShipping,
  Refresh,
  Person,
  PersonAdd,
  Star,
  Payment,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
  Photo,
  ArrowBack,
  ArrowForward,
  Close,
  SwapHoriz,
  Search,
  Chat,
  Send,
  AccountBalance,
  MoneyOff,
  MyLocation,
} from '@mui/icons-material';
import { ordersAPI, broadcastAPI, messagesAPI } from '../services/api';
import { fetchCompanyInfo } from '../services/companyInfo';
import { io } from 'socket.io-client';
import { loadKakaoMaps } from '../components/DriverLocationDialog';
import DriverLocationDialog from '../components/DriverLocationDialog';
import { estimateMinimumCost } from '../utils/costEstimator';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace('/api', '');

// 고객 주문서 신규 필드
const DISPATCH_TYPE_LABEL = { urgent: '긴급', round_trip: '왕복', waypoint: '경유' };
const DISPATCH_TYPE_COLOR = { urgent: 'error', round_trip: 'info', waypoint: 'secondary' };
const parseJsonArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
};
const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
// datetime-local input 용 (YYYY-MM-DDTHH:mm)
const toDtLocal = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DISPATCH_TYPES = [
  { key: 'urgent', label: '긴급' }, { key: 'round_trip', label: '왕복' }, { key: 'waypoint', label: '경유' },
];
const HANDLING_METHODS = ['지게차', '호이스트', '인력 제공', '수작업', '크레인'];
const VEHICLE_TYPE_OPTIONS = [
  '차종 무관', '카고', '카고/탑', '카고리프트', '윙바디', '윙바디/탑', '윙+', '탑',
  '탑리프트', '리프트', '리프트윙', '호로리프트', '호로', '추레라', '다마스', '라보',
  '무진동', '초장축', '냉장탑', '냉동탑', '냉장윙', '냉동탑리프트', '냉장윙리프트',
];
const WEIGHT_OPTIONS_NEW = [
  '0.3t', '0.5t', '1t', '1t초장축', '1.4t', '1.4t초장축', '2.5t', '3.5t', '3.5t광폭',
  '4t', '5t', '5플러스', '5t축', '5t플축', '8t', '9.5t', '11t', '14t', '16t', '18t',
  '22t', '25t',
];

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 8 && !digits.startsWith('0')) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

function OrderList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceStatusFilter, setPriceStatusFilter] = useState('');
  const [dispatchStatusFilter, setDispatchStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  // 페이지네이션 (서버사이드)
  const [page, setPage] = useState(0);            // 0-based (TablePagination 규약)
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]); // 업체 필터 드롭다운 목록
  // socket 핸들러는 mount 시점 클로저를 잡으므로, 항상 최신 필터/페이지로 조회하도록 ref 사용
  const filtersRef = useRef({});
  filtersRef.current = { priceStatusFilter, dispatchStatusFilter, clientFilter, page, rowsPerPage };
  const [unreadCounts, setUnreadCounts] = useState({});
  const [companyName, setCompanyName] = useState('');
  useEffect(() => { fetchCompanyInfo().then((c) => setCompanyName(c.company_name || '')).catch(() => {}); }, []);

  // 금액상태 변경 메뉴
  const [priceMenuAnchor, setPriceMenuAnchor] = useState(null);
  const [priceMenuOrder, setPriceMenuOrder] = useState(null);

  // 호출 다이얼로그
  const [broadcastDialog, setBroadcastDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [broadcastData, setBroadcastData] = useState({
    broadcast_price: '',
    broadcaster_phone: '',
    required_vehicle_types: [],
    required_weights: [],
    max_distance_km: '',
  });
  
  // 특정 개인 선택 기능
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [broadcastTargetType, setBroadcastTargetType] = useState('all'); // 'all' or 'selected'

  // 원클릭 재호출 다이얼로그 (배차실패 → 이전 조건 재사용)
  const [rebroadcastDialog, setRebroadcastDialog] = useState(false);
  const [rebroadcastOrder, setRebroadcastOrder] = useState(null);
  const [rebroadcastPrice, setRebroadcastPrice] = useState('');
  const [rebroadcastLoading, setRebroadcastLoading] = useState(false);

  // 직접 배차 다이얼로그 (호출 없이 기사 지정)
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignOrder, setAssignOrder] = useState(null);
  const [assignDriver, setAssignDriver] = useState(null);
  const [assignPrice, setAssignPrice] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // 신청자 목록 다이얼로그
  const [applicationsDialog, setApplicationsDialog] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  // 상세 보기 다이얼로그
  const [detailDialog, setDetailDialog] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);

  // 금액 제시 다이얼로그
  const [proposePriceDialog, setProposePriceDialog] = useState(false);
  const [proposePriceOrder, setProposePriceOrder] = useState(null);
  const [proposePriceValue, setProposePriceValue] = useState('');
  const [proposePriceLoading, setProposePriceLoading] = useState(false);
  // 최소경비 산출
  const [costEstimate, setCostEstimate] = useState(null);
  const [costEstimating, setCostEstimating] = useState(false);
  const [costEstimateError, setCostEstimateError] = useState('');

  // 사진 뷰어
  const [photoViewerDialog, setPhotoViewerDialog] = useState(false);
  const [photoViewerPhotos, setPhotoViewerPhotos] = useState([]);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);

  // 메시지
  const [messageDialog, setMessageDialog] = useState(false);
  const [messageOrder, setMessageOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [locationDialog, setLocationDialog] = useState(false);
  const [locationOrder, setLocationOrder] = useState(null);
  const openLocationDialog = (order) => { setLocationOrder(order); setLocationDialog(true); };
  const [newMessage, setNewMessage] = useState('');
  const [messageLoading, setMessageLoading] = useState(false);
  const messagesEndRef = React.useRef(null);
  const messageOrderRef = React.useRef(null);
  const messageDialogRef = React.useRef(false);

  const openMessageDialog = async (order) => {
    setMessageOrder(order);
    messageOrderRef.current = order;
    setMessageDialog(true);
    messageDialogRef.current = true;
    setNewMessage('');
    try {
      const res = await messagesAPI.getMessages(order.order_id);
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      loadUnreadCounts(); // 읽음 처리 후 배지 갱신
    } catch (err) {
      setMessages([]);
    }
  };

  const closeMessageDialog = () => {
    setMessageDialog(false);
    messageDialogRef.current = false;
    messageOrderRef.current = null;
  };

  const refreshMessages = async (orderId) => {
    try {
      const res = await messagesAPI.getMessages(orderId);
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {}
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !messageOrder) return;
    setMessageLoading(true);
    try {
      await messagesAPI.sendMessage(messageOrder.order_id, newMessage.trim());
      setNewMessage('');
      const res = await messagesAPI.getMessages(messageOrder.order_id);
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      alert('메시지 전송 실패');
    }
    setMessageLoading(false);
  };

  // 배차상태 변경
  const handleDispatchStatusChange = async (order, newStatus) => {
    const labels = { before_dispatch: '배차전', dispatch_ready: '배차준비', dispatch_failed: '배차실패', broadcasting: '호출중', assigned: '배차완료', in_progress: '배송중', completed: '완료', cancelled: '주문취소' };
    const label = labels[newStatus] || newStatus;
    if (!window.confirm(`${order.order_number} 주문의 배차상태를 [${label}](으)로 변경하시겠습니까?`)) return;
    try {
      await ordersAPI.updateStatus(order.order_id, newStatus);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '상태 변경에 실패했습니다.');
    }
  };

  // 금액상태 변경
  const handlePriceStatusChange = async (order, newPriceStatus, opts = {}) => {
    const { skipConfirm = false } = opts;
    const labels = { pending_price: '금액검토', payment_pending: '결재대기', deferred_payment: '추후결재', payment_completed: '결재완료', refund_pending: '환불대기', refund_completed: '환불완료', no_payment: '결재금액없음' };
    const label = labels[newPriceStatus] || newPriceStatus;
    if (!skipConfirm && !window.confirm(`${order.order_number} 주문의 금액상태를 [${label}](으)로 변경하시겠습니까?`)) return;
    try {
      await ordersAPI.updatePriceStatus(order.order_id, newPriceStatus);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '금액상태 변경에 실패했습니다.');
    }
  };

  // 단일/더블 클릭 분리용 타이머 (결재처리 아이콘)
  const priceClickTimerRef = useRef(null);
  const handlePriceIconClick = (e, order) => {
    // 더블클릭 가능성을 위해 단일 액션을 약간 지연
    const target = e.currentTarget;
    if (priceClickTimerRef.current) {
      clearTimeout(priceClickTimerRef.current);
      priceClickTimerRef.current = null;
    }
    priceClickTimerRef.current = setTimeout(() => {
      priceClickTimerRef.current = null;
      setPriceMenuAnchor(target);
      setPriceMenuOrder(order);
    }, 230);
  };
  const handlePriceIconDoubleClick = (e, order) => {
    e.stopPropagation();
    if (priceClickTimerRef.current) {
      clearTimeout(priceClickTimerRef.current);
      priceClickTimerRef.current = null;
    }
    setPriceMenuAnchor(null);
    setPriceMenuOrder(null);
    // 더블클릭 = 확실한 의도 → confirm 스킵
    handlePriceStatusChange(order, 'deferred_payment', { skipConfirm: true });
  };

  const openPhotoViewer = async (order, startIndex = 0) => {
    try {
      const response = await ordersAPI.getById(order.order_id);
      const photos = response.data.data?.photos || [];
      if (photos.length === 0) return alert('사진이 없습니다.');
      setPhotoViewerPhotos(photos);
      setPhotoViewerIndex(startIndex);
      setPhotoViewerDialog(true);
    } catch (err) {
      alert('사진을 불러올 수 없습니다.');
    }
  };

  const openProposePriceDialog = (order) => {
    setProposePriceOrder(order);
    setProposePriceValue(order.client_offered_price ? parseInt(order.client_offered_price) : '');
    setCostEstimate(null);
    setCostEstimateError('');
    setProposePriceDialog(true);
  };

  const handleEstimateCost = async () => {
    if (!proposePriceOrder) return;
    setCostEstimating(true);
    setCostEstimateError('');
    setCostEstimate(null);
    try {
      const wpAddresses = parseJsonArr(proposePriceOrder.waypoints)
        .map((w) => (typeof w === 'string' ? w : w?.address))
        .filter(Boolean);
      const result = await estimateMinimumCost({
        originAddress: proposePriceOrder.origin_address,
        destinationAddress: proposePriceOrder.destination_address,
        vehicleType: proposePriceOrder.required_vehicle_type,
        waypointAddresses: wpAddresses,
      });
      setCostEstimate(result);
    } catch (err) {
      setCostEstimateError(err.message || '경비 산출에 실패했습니다.');
    } finally {
      setCostEstimating(false);
    }
  };

  const applyEstimatedPrice = () => {
    if (costEstimate?.recommendedPrice) {
      setProposePriceValue(String(costEstimate.recommendedPrice));
    }
  };

  const handleProposePrice = async () => {
    if (!proposePriceValue) return alert('금액을 입력하세요.');
    setProposePriceLoading(true);
    try {
      await ordersAPI.proposePrice(proposePriceOrder.order_id, parseInt(proposePriceValue));
      alert('금액이 제시되었습니다. 고객에게 알림을 전송했습니다.');
      setProposePriceDialog(false);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '금액 제시에 실패했습니다.');
    } finally {
      setProposePriceLoading(false);
    }
  };

  // 금액 알림 — 고객 승인 없이 결재대기로 직행
  const handleNotifyPrice = async () => {
    if (!proposePriceValue) return alert('금액을 입력하세요.');
    if (!window.confirm('고객 승인 절차를 생략하고 결재대기 상태로 확정합니다. 진행하시겠습니까?')) return;
    setProposePriceLoading(true);
    try {
      await ordersAPI.notifyPrice(proposePriceOrder.order_id, parseInt(proposePriceValue));
      alert('금액이 결재대기로 확정되었습니다. 고객에게 알림을 전송했습니다.');
      setProposePriceDialog(false);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '금액 알림에 실패했습니다.');
    } finally {
      setProposePriceLoading(false);
    }
  };

  // 수정 다이얼로그
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({
    origin_address: '',
    origin_contact: '',
    origin_contact_name: '',
    destination_address: '',
    destination_contact: '',
    destination_contact_name: '',
    pickup_datetime: '',
    desired_delivery_datetime: '',
    client_offered_price: '',
    confirmed_price: '',
    required_vehicle_type: '',
    required_weight: '',
    vehicle_types: [],
    vehicle_weights: [],
    dispatch_types: [],
    waypoints: [],
    loading_methods: [],
    unloading_methods: [],
    special_notes: '',
    payment_date: '',
    remarks: '',
  });

  useEffect(() => {
    loadUnreadCounts();
    // 업체 필터 드롭다운 목록 (1회)
    ordersAPI.getCompanies()
      .then((res) => setCompanies(res.data.data || []))
      .catch(() => {});

    // Socket.io 연결 (재연결 시 join-admin 재실행 + 누락 이벤트 보완 위한 동기화)
    const socket = io(API_URL, { reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000 });
    socket.on('connect', () => {
      console.log('[Socket] 연결됨, admin-room 입장');
      socket.emit('join-admin');
      // 재연결 시 그동안 놓친 이벤트를 만회하기 위해 즉시 동기화
      loadOrders();
      loadUnreadCounts();
    });
    socket.on('disconnect', (reason) => {
      console.log('[Socket] 연결 끊김:', reason);
    });

    socket.on('order-updated', (data) => {
      console.log('주문 업데이트:', data);
      loadOrders();
    });

    // 운전자 행동 등에 대한 직원 푸시 알림 (수락/포기 등)
    socket.on('staff-push-notification', (data) => {
      try {
        if (Notification.permission === 'granted') {
          new Notification(data?.title || '알림', { body: data?.body || '' });
        }
      } catch (e) { /* ignore */ }
      // 상태가 함께 오는 경우 화면도 갱신
      loadOrders();
      loadUnreadCounts();
    });

    socket.on('new-application', (data) => {
      console.log('새 신청:', data);
      // 알림 표시
      if (Notification.permission === 'granted') {
        new Notification('🚚 새 배차 신청', {
          body: data.message,
        });
      }
      // 주문 목록 및 미확인 카운트 갱신
      loadOrders();
      loadUnreadCounts();
      // 신청 목록 다이얼로그가 열려있으면 갱신
      if (applicationsDialog && selectedOrder?.order_id === data.orderId) {
        loadApplications(data.orderId);
      }
    });

    socket.on('new-customer-order', (data) => {
      console.log('새 고객 주문:', data);
      if (Notification.permission === 'granted') {
        new Notification('📦 새 배차 신청', {
          body: data.message,
        });
      }
      loadOrders();
    });

    socket.on('order-message', (data) => {
      if (data.senderType === 'customer') {
        console.log('고객 메시지:', data);
        if (Notification.permission === 'granted') {
          new Notification('💬 고객 메시지', {
            body: `[${data.orderNumber}] ${data.senderName}: ${data.message}`,
          });
        }
        // 메시지 다이얼로그가 열려있고 같은 주문이면 새로고침
        if (messageDialogRef.current && messageOrderRef.current?.order_id === data.orderId) {
          refreshMessages(data.orderId);
        }
        loadUnreadCounts();
      }
    });

    // 브라우저 알림 권한 요청
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      socket.disconnect();
    };
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      // ref에서 읽어야 socket 이벤트로 호출돼도 현재 필터/페이지가 유지됨
      const f = filtersRef.current;
      const params = { page: f.page + 1, limit: f.rowsPerPage };
      if (f.priceStatusFilter) params.price_status = f.priceStatusFilter;
      if (f.dispatchStatusFilter) params.status = f.dispatchStatusFilter;
      if (f.clientFilter) params.company = f.clientFilter;
      const response = await ordersAPI.getAll(params);
      setOrders(response.data.data || []);
      if (response.data.pagination) setTotal(response.data.pagination.total);
    } catch (err) {
      console.error('주문 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const res = await messagesAPI.getUnreadAll();
      setUnreadCounts(res.data.data || {});
    } catch (err) {}
  };

  useEffect(() => {
    loadOrders();
  }, [priceStatusFilter, dispatchStatusFilter, clientFilter, page, rowsPerPage]);

  const formatPrice = (price) => {
    if (!price) return '-';
    return Math.floor(price).toLocaleString() + '원';
  };

  // 금액상태 맵 (글자색)
  const priceStatusMap = {
    pending_price: { label: '금액검토', textColor: '#757575' },
    payment_pending: { label: '결재대기', textColor: '#ed6c02' },
    deferred_payment: { label: '추후결재', textColor: '#9c27b0' },
    payment_completed: { label: '결재완료', textColor: '#2e7d32' },
    refund_pending: { label: '환불대기', textColor: '#d32f2f' },
    refund_completed: { label: '환불완료', textColor: '#757575' },
    no_payment: { label: '결재금액없음', textColor: '#757575' },
  };

  // 배차상태 맵 (글자색)
  const dispatchStatusMap = {
    before_dispatch: { label: '배차전', textColor: '#757575' },
    dispatch_ready: { label: '배차준비', textColor: '#0288d1' },
    broadcasting: { label: '호출중', textColor: '#ed6c02' },
    dispatch_failed: { label: '배차실패', textColor: '#d32f2f' },
    assigned: { label: '배차완료', textColor: '#1976d2' },
    in_progress: { label: '배송중', textColor: '#1976d2' },
    completed: { label: '완료', textColor: '#2e7d32' },
    cancelled: { label: '주문취소', textColor: '#d32f2f' },
  };

  const getPriceStatusChip = (priceStatus) => {
    const info = priceStatusMap[priceStatus] || { label: priceStatus || '-', textColor: '#757575' };
    return <Chip label={info.label} size="small" variant="outlined" sx={{ border: 'none', color: info.textColor, fontWeight: 600, px: 0 }} />;
  };

  const getDispatchStatusChip = (status) => {
    const info = dispatchStatusMap[status] || { label: status || '-', textColor: '#757575' };
    return <Chip label={info.label} size="small" variant="outlined" sx={{ border: 'none', color: info.textColor, fontWeight: 600, px: 0 }} />;
  };

  // 호출 다이얼로그 열기
  const openBroadcastDialog = async (order) => {
    setSelectedOrder(order);
    // 기사에게 제시할 배차금액 초기값 (고객 청구금액 참고)
    const initialPrice = order.confirmed_price || order.client_offered_price || '';
    // 로그인한 직원의 전화번호를 기본값으로 설정
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    // 고객 요구 차종 기본 체크 — 신규 vehicle_types(JSON 다중) 우선, 없으면 required_vehicle_type(콤마)
    const vTypesJson = parseJsonArr(order.vehicle_types);
    const orderTypes = vTypesJson.length > 0
      ? vTypesJson
      : (order.required_vehicle_type ? order.required_vehicle_type.split(',').map(t => t.trim()) : []);
    // 고객 요구 톤수 기본 체크 — 신규 vehicle_weights(JSON 다중, 문자열) 우선
    const vWeightsJson = parseJsonArr(order.vehicle_weights);
    const defaultWeights = vWeightsJson.length > 0
      ? vWeightsJson.map(String)
      : (order.required_weight ? [String(order.required_weight)] : []);
    setBroadcastData({
      broadcast_price: initialPrice ? parseInt(initialPrice, 10) : '',
      broadcaster_phone: currentUser.phone || '',
      required_vehicle_types: orderTypes,
      required_weights: defaultWeights,
      max_distance_km: '',
    });
    // 초기화
    setBroadcastTargetType('all');
    setSelectedDriverIds([]);
    
    // 운전자 목록 로드
    try {
      const response = await fetch(`${API_URL}/api/drivers?is_active=true`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAllDrivers(data.data || []);
    } catch (err) {
      console.error('운전자 목록 로드 실패:', err);
    }
    
    setBroadcastDialog(true);
  };

  // 출발지 주소 → 좌표 (Kakao Geocoder)
  const geocodeAddress = async (address) => {
    try {
      const kakao = await loadKakaoMaps();
      if (!kakao?.maps?.services?.Geocoder) return null;
      return await new Promise((resolve) => {
        const geocoder = new kakao.maps.services.Geocoder();
        geocoder.addressSearch(address, (result, status) => {
          if (status === kakao.maps.services.Status.OK && result[0]) {
            resolve({ lat: parseFloat(result[0].y), lng: parseFloat(result[0].x) });
          } else {
            resolve(null);
          }
        });
      });
    } catch (e) {
      return null;
    }
  };

  // 호출 시작
  const handleBroadcast = async () => {
    try {
      // 거리 필터 사용 시 출발지 좌표 지오코딩
      let originCoords = null;
      let distanceKm = broadcastTargetType === 'all' ? broadcastData.max_distance_km : '';
      if (distanceKm) {
        originCoords = await geocodeAddress(selectedOrder.origin_address);
        if (!originCoords) {
          if (!window.confirm('출발지 주소의 좌표를 찾지 못해 거리 필터를 적용할 수 없습니다.\n거리 제한 없이 호출할까요?')) return;
          distanceKm = '';
        }
      }

      const payload = {
        broadcast_price: broadcastData.broadcast_price,
        broadcaster_phone: broadcastData.broadcaster_phone,
        required_vehicle_type: broadcastData.required_vehicle_types.length > 0 ? broadcastData.required_vehicle_types.join(',') : null,
        required_weights: broadcastData.required_weights.length > 0 ? broadcastData.required_weights : null,
        target_type: broadcastTargetType,
        target_driver_ids: broadcastTargetType === 'selected' ? selectedDriverIds : null,
        max_distance_km: distanceKm || null,
        origin_lat: originCoords?.lat ?? null,
        origin_lng: originCoords?.lng ?? null,
      };
      const bcRes = await broadcastAPI.broadcast(selectedOrder.order_id, payload);
      if (bcRes.data?.message) alert(bcRes.data.message);
      setBroadcastDialog(false);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '호출에 실패했습니다.');
    }
  };

  // 호출 취소
  const handleCancelBroadcast = async (order) => {
    if (!window.confirm('호출을 취소하시겠습니까?')) return;
    try {
      await broadcastAPI.cancelBroadcast(order.order_id);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '취소에 실패했습니다.');
      // stale state 가능성 — 최신 상태로 새로고침
      loadOrders();
    }
  };

  // 원클릭 재호출 — 이전 호출 조건으로 즉시 재호출 (금액만 조정 가능)
  const openRebroadcastDialog = (order) => {
    // 이전 호출이 특정 운전자 대상이었으면 대상 목록이 저장되지 않으므로 일반 호출 다이얼로그로
    if (Number(order.broadcast_to_all) !== 1) {
      openBroadcastDialog(order);
      return;
    }
    setRebroadcastOrder(order);
    const prev = order.broadcast_price || order.confirmed_price || '';
    setRebroadcastPrice(prev ? parseInt(prev, 10) : '');
    setRebroadcastDialog(true);
  };

  const handleRebroadcast = async () => {
    if (!rebroadcastPrice) return;
    setRebroadcastLoading(true);
    try {
      const order = rebroadcastOrder;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const weights = parseJsonArr(order.vehicle_weights).map(String);
      const payload = {
        broadcast_price: rebroadcastPrice,
        broadcaster_phone: order.broadcaster_phone || currentUser.phone || '',
        required_vehicle_type: order.required_vehicle_type || null,
        required_weights: weights.length > 0 ? weights : null,
        target_type: 'all',
        target_driver_ids: null,
        max_distance_km: order.max_distance_km || null,
        origin_lat: order.origin_lat || null,
        origin_lng: order.origin_lng || null,
      };
      const res = await broadcastAPI.broadcast(order.order_id, payload);
      if (res.data?.message) alert(res.data.message);
      setRebroadcastDialog(false);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '재호출에 실패했습니다.');
    } finally {
      setRebroadcastLoading(false);
    }
  };

  // 직접 배차 — 다이얼로그 열기 (기사 목록 로드)
  const openAssignDialog = async (order) => {
    setAssignOrder(order);
    setAssignDriver(null);
    const initialPrice = order.confirmed_price || order.client_offered_price || '';
    setAssignPrice(initialPrice ? parseInt(initialPrice, 10) : '');
    try {
      const response = await fetch(`${API_URL}/api/drivers?is_active=true`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAllDrivers(data.data || []);
    } catch (err) {
      console.error('운전자 목록 로드 실패:', err);
    }
    setAssignDialog(true);
  };

  // 직접 배차 확정
  const handleDirectAssign = async () => {
    if (!assignDriver) return;
    const priceText = assignPrice ? Number(assignPrice).toLocaleString() + '원' : '기존 금액 유지';
    if (!window.confirm(`${assignDriver.name} 기사님에게 바로 배차하시겠습니까?\n배차금액(기사): ${priceText}`)) return;
    setAssignLoading(true);
    try {
      await ordersAPI.assign(assignOrder.order_id, assignDriver.driver_id, {
        confirmed_price: assignPrice || null,
      });
      setAssignDialog(false);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '배차에 실패했습니다.');
    } finally {
      setAssignLoading(false);
    }
  };

  // 신청자 목록 보기
  const openApplicationsDialog = async (order) => {
    setSelectedOrder(order);
    setApplicationsDialog(true);
    loadApplications(order.order_id);
  };

  const loadApplications = async (orderId) => {
    setLoadingApplications(true);
    try {
      const response = await broadcastAPI.getApplications(orderId);
      setApplications(response.data.data || []);
    } catch (err) {
      console.error('신청 목록 로드 실패:', err);
    } finally {
      setLoadingApplications(false);
    }
  };

  // 신청 승인
  const handleApproveApplication = async (applicationId) => {
    try {
      await broadcastAPI.approveApplication(selectedOrder.order_id, applicationId);
      setApplicationsDialog(false);
      loadOrders();
      alert('배차가 완료되었습니다.');
    } catch (err) {
      alert(err.response?.data?.message || '배차에 실패했습니다.');
    }
  };

  // 배차 취소
  const handleCancelAssignment = async (order) => {
    if (!window.confirm('배차를 취소하시겠습니까?')) return;
    try {
      await broadcastAPI.cancelAssignment(order.order_id);
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '취소에 실패했습니다.');
    }
  };

  // 송금 완료
  const handleConfirmPayment = async (order) => {
    const priceText = (order.confirmed_price || order.broadcast_price || 0).toLocaleString();
    if (!window.confirm(`${order.driver_name}님에게 ${priceText}원 송금을 완료하셨습니까?`)) return;
    try {
      await ordersAPI.confirmPayment(order.order_id);
      loadOrders();
      alert('송금 완료가 전달되었습니다.');
    } catch (err) {
      alert(err.response?.data?.message || '처리에 실패했습니다.');
    }
  };

  // 주문 취소 (결재완료 상태면 환불대기로 자동 전환)
  const handleCancelOrder = async (order) => {
    if (!window.confirm(`주문 ${order.order_number}을(를) 취소하시겠습니까?`)) return;
    try {
      await ordersAPI.updateStatus(order.order_id, 'cancelled');
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '주문 취소에 실패했습니다.');
    }
  };

  // 환불 완료 처리 (price_status → refund_completed, status → cancelled)
  const handleRefundComplete = async (order) => {
    if (!window.confirm(`${order.order_number} 환불 완료 처리하시겠습니까? 주문이 취소 상태로 변경됩니다.`)) return;
    try {
      await ordersAPI.updatePriceStatus(order.order_id, 'refund_completed');
      await ordersAPI.updateStatus(order.order_id, 'cancelled');
      loadOrders();
    } catch (err) {
      alert(err.response?.data?.message || '환불 완료 처리에 실패했습니다.');
    }
  };

  // 상세 보기
  const openDetailDialog = async (order) => {
    try {
      const response = await ordersAPI.getById(order.order_id);
      setOrderDetail(response.data.data);
      setDetailDialog(true);
    } catch (err) {
      alert('상세 정보를 불러올 수 없습니다.');
    }
  };

  // 수정 다이얼로그 열기
  const [editPhotos, setEditPhotos] = useState([]);

  const openEditDialog = async (order) => {
    setSelectedOrder(order);
    // pickup_datetime 또는 pickup_time을 datetime-local 형식으로 변환
    let pickupValue = '';
    const rawPickup = order.pickup_datetime || order.pickup_time;
    if (rawPickup) {
      try {
        const dt = new Date(rawPickup);
        if (!isNaN(dt.getTime())) {
          const year = dt.getFullYear();
          const month = String(dt.getMonth() + 1).padStart(2, '0');
          const day = String(dt.getDate()).padStart(2, '0');
          const hours = String(dt.getHours()).padStart(2, '0');
          const minutes = String(dt.getMinutes()).padStart(2, '0');
          pickupValue = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      } catch (e) {
        console.error('날짜 변환 오류:', e);
        pickupValue = '';
      }
    }
    setEditData({
      origin_address: order.origin_address || '',
      origin_contact: order.origin_contact || '',
      origin_contact_name: order.origin_contact_name || '',
      destination_address: order.destination_address || '',
      destination_contact: order.destination_contact || '',
      destination_contact_name: order.destination_contact_name || '',
      pickup_datetime: pickupValue || toDtLocal(order.pickup_time),
      desired_delivery_datetime: toDtLocal(order.desired_delivery_datetime),
      client_offered_price: order.client_offered_price ? parseInt(order.client_offered_price, 10) : '',
      confirmed_price: order.confirmed_price ? parseInt(order.confirmed_price, 10) : '',
      required_vehicle_type: order.required_vehicle_type || '',
      required_weight: order.required_weight || '',
      vehicle_types: parseJsonArr(order.vehicle_types),
      vehicle_weights: parseJsonArr(order.vehicle_weights),
      dispatch_types: parseJsonArr(order.dispatch_types),
      waypoints: parseJsonArr(order.waypoints),
      loading_methods: parseJsonArr(order.loading_methods),
      unloading_methods: parseJsonArr(order.unloading_methods),
      special_notes: order.special_notes || '',
      payment_date: order.payment_date ? order.payment_date.slice(0, 16) : '',
      remarks: order.remarks || '',
    });
    // 사진 불러오기
    try {
      const response = await ordersAPI.getById(order.order_id);
      setEditPhotos(response.data.data?.photos || []);
    } catch (e) {
      setEditPhotos([]);
    }
    setEditDialog(true);
  };

  // 주문 수정 저장
  const handleEditSave = async () => {
    try {
      await ordersAPI.update(selectedOrder.order_id, editData);
      setEditDialog(false);
      loadOrders();
      alert('주문이 수정되었습니다.');
    } catch (err) {
      alert(err.response?.data?.message || '수정에 실패했습니다.');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          주문 관리
        </Typography>
        <Box display="flex" gap={1}>
          <IconButton onClick={loadOrders} title="새로고침">
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/orders/new')}
          >
            주문 생성
          </Button>
        </Box>
      </Box>

      {/* 필터 */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>금액 상태</InputLabel>
              <Select
                value={priceStatusFilter}
                onChange={(e) => { setPriceStatusFilter(e.target.value); setPage(0); }}
                label="금액 상태"
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="pending_price">금액검토</MenuItem>
                <MenuItem value="payment_pending">결재대기</MenuItem>
                <MenuItem value="deferred_payment">추후결재</MenuItem>
                <MenuItem value="payment_completed">결재완료</MenuItem>
                <MenuItem value="refund_pending">환불대기</MenuItem>
                <MenuItem value="refund_completed">환불완료</MenuItem>
                <MenuItem value="no_payment">결재금액없음</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>배차 상태</InputLabel>
              <Select
                value={dispatchStatusFilter}
                onChange={(e) => { setDispatchStatusFilter(e.target.value); setPage(0); }}
                label="배차 상태"
              >
                <MenuItem value="">전체</MenuItem>
                <MenuItem value="before_dispatch">배차전</MenuItem>
                <MenuItem value="dispatch_ready">배차준비</MenuItem>
                <MenuItem value="broadcasting">호출중</MenuItem>
                <MenuItem value="assigned">배차완료</MenuItem>
                <MenuItem value="in_progress">배송중</MenuItem>
                <MenuItem value="completed">완료</MenuItem>
                <MenuItem value="cancelled">주문취소</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>고객(업체) 필터</InputLabel>
              <Select
                value={clientFilter}
                onChange={(e) => { setClientFilter(e.target.value); setPage(0); }}
                label="고객(업체) 필터"
              >
                <MenuItem value="">전체</MenuItem>
                {companies.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              총 {total}건
            </Typography>
          </Grid>
        </Grid>
      </Card>

      {/* 주문 목록 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>주문번호</TableCell>
              <TableCell>업체명</TableCell>
              <TableCell>출발지</TableCell>
              <TableCell>도착지</TableCell>
              <TableCell align="right">제시금액(고객)</TableCell>
              <TableCell align="right">배차금액(기사)</TableCell>
              <TableCell align="right">수익금</TableCell>
              <TableCell>운전자</TableCell>
              <TableCell>기사 송금일</TableCell>
              <TableCell>금액상태</TableCell>
              <TableCell>배차상태</TableCell>
              <TableCell align="center">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  주문이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => {
                const proposedPrice = Number(order.client_offered_price) || 0;  // 제시금액(고객)
                const driverPrice = Number(order.confirmed_price) || 0;      // 배차금액(기사)
                const profit = proposedPrice - driverPrice;

                return (
                  <TableRow key={order.order_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {order.order_number}
                        {unreadCounts[order.order_id] > 0 && (
                          <Tooltip title={`미확인 메시지 ${unreadCounts[order.order_id]}건`}>
                            <IconButton size="small" sx={{ p: 0.3 }} onClick={(e) => { e.stopPropagation(); openMessageDialog(order); }}>
                              <Badge badgeContent={unreadCounts[order.order_id]} color="error" max={99}>
                                <Chat fontSize="small" sx={{ color: '#f44336', fontSize: 16 }} />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{order.company_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.origin_address}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.destination_address}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#e65100', fontWeight: 600 }}>
                      {formatPrice(proposedPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#1976d2', fontWeight: 600 }}>
                      {formatPrice(driverPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: profit >= 0 ? '#4CAF50' : '#f44336', fontWeight: 600 }}>
                      {profit >= 0 ? '+' : ''}{formatPrice(profit)}
                    </TableCell>
                    <TableCell>{order.driver_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: order.payment_date ? 'success.main' : 'text.disabled' }}>
                      {order.payment_date ? order.payment_date.slice(0, 16).replace('T', ' ') : '-'}
                    </TableCell>
                    <TableCell>{getPriceStatusChip(order.price_status)}</TableCell>
                    <TableCell>{getDispatchStatusChip(order.status)}</TableCell>
                    <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="상세보기">
                        <IconButton size="small" onClick={() => openDetailDialog(order)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {order.photo_count > 0 && (
                        <Tooltip title={`사진 ${order.photo_count}장`}>
                          <IconButton size="small" color="info" onClick={() => openPhotoViewer(order)}>
                            <Photo fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* === 금액상태 관련 버튼 === */}
                      {/* 금액검토: 금액 제시 (활성) */}
                      {order.price_status === 'pending_price' && (
                        <Tooltip title="금액 제시">
                          <IconButton size="small" color="warning" onClick={() => openProposePriceDialog(order)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 금액 제시 후 고객 응답 대기: 비활성(회색) */}
                      {order.price_status === 'payment_pending' && !order.customer_accepted_at && (
                        <Tooltip title="고객 응답 대기중">
                          <span>
                            <IconButton size="small" disabled>
                              <Payment fontSize="small" sx={{ color: '#bdbdbd' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {/* 결재대기 (고객 수락 후): 추후결재 또는 입금처리 선택 — 더블클릭 시 추후결재로 직행 */}
                      {order.price_status === 'payment_pending' && order.customer_accepted_at && (
                        <Tooltip title="결재 처리 (더블클릭: 추후결재)">
                          <IconButton size="small"
                            onClick={(e) => handlePriceIconClick(e, order)}
                            onDoubleClick={(e) => handlePriceIconDoubleClick(e, order)}
                            sx={{ fontSize: 16 }}>
                            💰
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 추후결재: 입금처리 — 더블클릭은 동일 상태 무의미하지만 일관성 위해 추가 안 함 */}
                      {order.price_status === 'deferred_payment' && (
                        <Tooltip title="결재 처리">
                          <IconButton size="small" onClick={(e) => { setPriceMenuAnchor(e.currentTarget); setPriceMenuOrder(order); }}
                            sx={{ fontSize: 16 }}>
                            💰
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 결재완료: 추후결재로 변경 가능 — 더블클릭 시 추후결재로 직행 */}
                      {['dispatch_ready', 'broadcasting', 'assigned'].includes(order.status) && order.price_status === 'payment_completed' && (
                        <Tooltip title="결재 처리 (더블클릭: 추후결재)">
                          <IconButton size="small"
                            onClick={(e) => handlePriceIconClick(e, order)}
                            onDoubleClick={(e) => handlePriceIconDoubleClick(e, order)}
                            sx={{ fontSize: 16 }}>
                            💰
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* 환불대기: 환불완료 + 주문취소 */}
                      {order.price_status === 'refund_pending' && (
                        <Tooltip title="환불 완료 (주문취소)">
                          <IconButton size="small" sx={{ color: '#4caf50' }} onClick={() => handleRefundComplete(order)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* === 배차상태 관련 버튼 === */}
                      {/* 배차전: 결재처리 완료(추후결재/결재완료) 이후에만 수정, 주문취소는 항상 */}
                      {order.status === 'before_dispatch' && (() => {
                        const canEdit =
                          order.price_status === 'deferred_payment' ||
                          order.price_status === 'payment_completed';
                        return (
                          <>
                            {canEdit && (
                              <Tooltip title="수정">
                                <IconButton size="small" color="primary" onClick={() => openEditDialog(order)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="주문 취소">
                              <IconButton size="small" color="error" onClick={() => handleCancelOrder(order)}>
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        );
                      })()}

                      {/* 배차준비: 수정, 호출, 주문취소 */}
                      {order.status === 'dispatch_ready' && (
                        <>
                          <Tooltip title="수정">
                            <IconButton size="small" color="primary" onClick={() => openEditDialog(order)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(() => {
                            const priceReady = order.client_offered_price && order.confirmed_price;
                            const paymentReady = ['payment_completed', 'deferred_payment'].includes(order.price_status);
                            const canBroadcast = priceReady && paymentReady;
                            return (
                              <Tooltip title={canBroadcast ? "배차 호출" : "제시금액/배차금액 입력 및 결재처리 필요"}>
                                <span>
                                <IconButton size="small" disabled={!canBroadcast}
                                  sx={{ color: canBroadcast ? '#ed6c02' : '#bdbdbd' }}
                                  onClick={() => canBroadcast && openBroadcastDialog(order)}>
                                  <Campaign fontSize="small" />
                                </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          {(() => {
                            const paymentReady = ['payment_completed', 'deferred_payment'].includes(order.price_status);
                            return (
                              <Tooltip title={paymentReady ? "직접 배차 (호출 없이 기사 지정)" : "결재처리 후 직접 배차 가능"}>
                                <span>
                                <IconButton size="small" disabled={!paymentReady}
                                  sx={{ color: paymentReady ? '#2e7d32' : '#bdbdbd' }}
                                  onClick={() => paymentReady && openAssignDialog(order)}>
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Tooltip title="주문 취소">
                            <IconButton size="small" color="error" onClick={() => handleCancelOrder(order)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* 배차실패: 재호출/다시 호출/수정 가능 */}
                      {order.status === 'dispatch_failed' && (
                        <>
                          <Tooltip title="수정">
                            <IconButton size="small" color="primary" onClick={() => openEditDialog(order)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {order.broadcast_price && ['payment_completed', 'deferred_payment'].includes(order.price_status) && (
                            <Tooltip title="원클릭 재호출 (이전 조건 재사용, 금액 조정 가능)">
                              <IconButton size="small" sx={{ color: '#d84315' }}
                                onClick={() => openRebroadcastDialog(order)}>
                                <Refresh fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(() => {
                            const priceReady = order.client_offered_price && order.confirmed_price;
                            const paymentReady = ['payment_completed', 'deferred_payment'].includes(order.price_status);
                            const canBroadcast = priceReady && paymentReady;
                            return (
                              <Tooltip title={canBroadcast ? "다시 배차 호출" : "제시금액/배차금액 입력 및 결재처리 필요"}>
                                <span>
                                <IconButton size="small" disabled={!canBroadcast}
                                  sx={{ color: canBroadcast ? '#ed6c02' : '#bdbdbd' }}
                                  onClick={() => canBroadcast && openBroadcastDialog(order)}>
                                  <Campaign fontSize="small" />
                                </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          {(() => {
                            const paymentReady = ['payment_completed', 'deferred_payment'].includes(order.price_status);
                            return (
                              <Tooltip title={paymentReady ? "직접 배차 (호출 없이 기사 지정)" : "결재처리 후 직접 배차 가능"}>
                                <span>
                                <IconButton size="small" disabled={!paymentReady}
                                  sx={{ color: paymentReady ? '#2e7d32' : '#bdbdbd' }}
                                  onClick={() => paymentReady && openAssignDialog(order)}>
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Tooltip title="주문 취소">
                            <IconButton size="small" color="error" onClick={() => handleCancelOrder(order)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* 호출중: 신청자 보기, 호출 취소 */}
                      {order.status === 'broadcasting' && (
                        <>
                          <Tooltip title="신청자 보기">
                            <IconButton size="small" color="primary" onClick={() => openApplicationsDialog(order)}>
                              <Person fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="호출 취소">
                            <IconButton size="small" color="error" onClick={() => handleCancelBroadcast(order)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* 배차완료: 송금, 배차 취소 */}
                      {order.status === 'assigned' && (
                        <>
                          {!order.payment_confirmed ? (
                            <Tooltip title="송금 완료">
                              <IconButton size="small" color="success" onClick={() => handleConfirmPayment(order)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="송금 완료됨">
                              <IconButton size="small" disabled>
                                <Payment fontSize="small" sx={{ color: '#ccc' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="배차 취소">
                            <IconButton size="small" color="error" onClick={() => handleCancelAssignment(order)}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* 배송중: 수정, 위치보기 (운전자 현재 위치 + 경로) */}
                      {order.status === 'in_progress' && (
                        <>
                          <Tooltip title="수정">
                            <IconButton size="small" color="primary" onClick={() => openEditDialog(order)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="위치보기">
                            <IconButton size="small" sx={{ color: '#1565c0' }} onClick={() => openLocationDialog(order)}>
                              <MyLocation fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {/* 완료: 송금 + 송금일 수정 */}
                      {order.status === 'completed' && (
                        <>
                          {!order.payment_confirmed ? (
                            <Tooltip title="송금 완료">
                              <IconButton size="small" color="success" onClick={() => handleConfirmPayment(order)}>
                                <Payment fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="송금 완료됨">
                              <IconButton size="small" disabled>
                                <Payment fontSize="small" sx={{ color: '#ccc' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="송금일/비고 수정">
                            <IconButton size="small" color="primary" onClick={() => openEditDialog(order)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="페이지당 행"
          labelDisplayedRows={({ from, to, count }) => `전체 ${count}건 중 ${from}–${to}`}
        />
      </TableContainer>

      {/* 원클릭 재호출 다이얼로그 */}
      <Dialog open={rebroadcastDialog} onClose={() => setRebroadcastDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>원클릭 재호출</DialogTitle>
        <DialogContent>
          {rebroadcastOrder && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">{rebroadcastOrder.order_number}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {rebroadcastOrder.origin_address} → {rebroadcastOrder.destination_address}
                </Typography>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                이전 호출 조건 그대로 다시 호출합니다.<br />
                차종: {rebroadcastOrder.required_vehicle_type || '전체'} ·
                거리: {rebroadcastOrder.max_distance_km ? `${rebroadcastOrder.max_distance_km}km 이내` : '제한 없음'} ·
                대상: 전체 운전자
              </Alert>
              {rebroadcastOrder.client_offered_price && (() => {
                const customerPrice = parseInt(rebroadcastOrder.client_offered_price, 10);
                const profit = rebroadcastPrice ? customerPrice - Number(rebroadcastPrice) : null;
                return (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    제시금액(고객): <strong>{customerPrice.toLocaleString()}원</strong>
                    {profit !== null && (
                      <span style={{ color: profit >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 700 }}>
                        {' '}· 예상 수익 {profit >= 0 ? '+' : ''}{profit.toLocaleString()}원
                      </span>
                    )}
                  </Typography>
                );
              })()}
              <TextField
                fullWidth size="small" label="호출 금액"
                value={rebroadcastPrice ? parseInt(rebroadcastPrice, 10).toLocaleString() : ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '');
                  setRebroadcastPrice(v ? parseInt(v, 10) : '');
                }}
                InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                helperText={rebroadcastOrder.broadcast_price
                  ? `이전 호출 금액: ${parseInt(rebroadcastOrder.broadcast_price, 10).toLocaleString()}원`
                  : ''}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {[10000, 20000, 50000].map((add) => (
                  <Chip key={add} size="small" clickable
                    label={`+${(add / 10000)}만`}
                    onClick={() => setRebroadcastPrice((Number(rebroadcastPrice) || 0) + add)}
                  />
                ))}
                <Chip size="small" clickable label="초기화"
                  onClick={() => setRebroadcastPrice(rebroadcastOrder.broadcast_price ? parseInt(rebroadcastOrder.broadcast_price, 10) : '')}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebroadcastDialog(false)}>닫기</Button>
          <Button variant="contained" color="warning" onClick={handleRebroadcast}
            disabled={rebroadcastLoading || !rebroadcastPrice}>
            {rebroadcastLoading ? <CircularProgress size={20} /> : '재호출 시작'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 직접 배차 다이얼로그 */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>직접 배차</DialogTitle>
        <DialogContent>
          {assignOrder && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">{assignOrder.order_number}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {assignOrder.origin_address} → {assignOrder.destination_address}
              </Typography>
            </Box>
          )}
          <Autocomplete
            options={allDrivers}
            value={assignDriver}
            onChange={(e, v) => setAssignDriver(v)}
            getOptionLabel={(d) =>
              `${d.name} (${d.vehicle_number || '-'} · ${d.vehicle_type || '-'}${(d.weight_capacity || d.vehicle_weight) ? ' ' + (d.weight_capacity || d.vehicle_weight) : ''})`
            }
            isOptionEqualToValue={(o, v) => o.driver_id === v.driver_id}
            noOptionsText="활성 기사가 없습니다"
            renderInput={(params) => <TextField {...params} label="기사 선택" size="small" autoFocus />}
            sx={{ mb: 2, mt: 0.5 }}
          />
          <TextField
            fullWidth size="small" label="배차금액(기사)"
            value={assignPrice ? parseInt(assignPrice, 10).toLocaleString() : ''}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, '');
              setAssignPrice(v ? parseInt(v, 10) : '');
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
            helperText="비워두면 기존 배차금액이 유지됩니다."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>닫기</Button>
          <Button variant="contained" color="success" onClick={handleDirectAssign}
            disabled={assignLoading || !assignDriver}>
            {assignLoading ? <CircularProgress size={20} /> : '배차 확정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 금액상태 변경 메뉴 */}
      <Menu
        anchorEl={priceMenuAnchor}
        open={Boolean(priceMenuAnchor)}
        onClose={() => { setPriceMenuAnchor(null); setPriceMenuOrder(null); }}
      >
        {/* 결재대기: 결재완료, 추후결재 */}
        {priceMenuOrder?.price_status === 'payment_pending' && [
          <MenuItem key="pay" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'payment_completed'); }}>
            <ListItemIcon><AccountBalance fontSize="small" sx={{ color: '#4caf50' }} /></ListItemIcon>
            결재완료 (입금처리)
          </MenuItem>,
          <MenuItem key="defer" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'deferred_payment'); }}>
            <ListItemIcon><MoneyOff fontSize="small" color="secondary" /></ListItemIcon>
            추후결재
          </MenuItem>
        ]}
        {/* 결재완료: 추후결재, 결재대기 */}
        {priceMenuOrder?.price_status === 'payment_completed' && [
          <MenuItem key="defer" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'deferred_payment'); }}>
            <ListItemIcon><MoneyOff fontSize="small" color="secondary" /></ListItemIcon>
            추후결재
          </MenuItem>,
          <MenuItem key="pending" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'payment_pending'); }}>
            <ListItemIcon><Payment fontSize="small" sx={{ color: '#ed6c02' }} /></ListItemIcon>
            결재대기
          </MenuItem>
        ]}
        {/* 추후결재: 결재완료, 결재대기 */}
        {priceMenuOrder?.price_status === 'deferred_payment' && [
          <MenuItem key="pay" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'payment_completed'); }}>
            <ListItemIcon><AccountBalance fontSize="small" sx={{ color: '#4caf50' }} /></ListItemIcon>
            결재완료 (입금처리)
          </MenuItem>,
          <MenuItem key="pending" onClick={() => { const o = priceMenuOrder; setPriceMenuAnchor(null); setPriceMenuOrder(null); handlePriceStatusChange(o, 'payment_pending'); }}>
            <ListItemIcon><Payment fontSize="small" sx={{ color: '#ed6c02' }} /></ListItemIcon>
            결재대기
          </MenuItem>
        ]}
      </Menu>

      {/* 호출 다이얼로그 */}
      <Dialog open={broadcastDialog} onClose={() => setBroadcastDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>배차 호출</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedOrder && (
              <>
                {/* 주문 정보 */}
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>📦 주문 정보</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">상차지</Typography>
                      <Typography variant="body1">{selectedOrder.origin_address}</Typography>
                      <Typography variant="body2">
                        {selectedOrder.origin_contact_name ? <strong>{selectedOrder.origin_contact_name}</strong> : ''}
                        {selectedOrder.origin_contact_name && selectedOrder.origin_contact ? ' · ' : ''}
                        <span style={{ color: '#1976d2' }}>{selectedOrder.origin_contact || '-'}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">하차지</Typography>
                      <Typography variant="body1">{selectedOrder.destination_address}</Typography>
                      <Typography variant="body2">
                        {selectedOrder.destination_contact_name ? <strong>{selectedOrder.destination_contact_name}</strong> : ''}
                        {selectedOrder.destination_contact_name && selectedOrder.destination_contact ? ' · ' : ''}
                        <span style={{ color: '#1976d2' }}>{selectedOrder.destination_contact || '-'}</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">📅 상차 일시</Typography>
                        <Typography variant="body1" fontWeight={600} color="#2e7d32">
                          {fmtDateTime(selectedOrder.pickup_time || selectedOrder.pickup_datetime) || '미지정'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">📅 하차 일시</Typography>
                        <Typography variant="body1" fontWeight={600} color="#FF9800">
                          {fmtDateTime(selectedOrder.desired_delivery_datetime) || '미지정'}
                        </Typography>
                      </Box>
                    </Grid>
                    {/* 경유지 */}
                    {parseJsonArr(selectedOrder.waypoints).length > 0 && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#f3e5f5', borderRadius: 1, borderLeft: '4px solid #9c27b0' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>🚏 경유지</Typography>
                          {parseJsonArr(selectedOrder.waypoints).map((w, i) => {
                            const addr = typeof w === 'string' ? w : w?.address;
                            const cn = typeof w === 'object' ? w?.contact_name : '';
                            const c = typeof w === 'object' ? w?.contact : '';
                            return (
                              <Box key={i} sx={{ mb: 0.5 }}>
                                <Typography variant="body2">{i + 1}. {addr}</Typography>
                                {(cn || c) && (
                                  <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                                    {cn ? <strong>{cn}</strong> : ''}
                                    {cn && c ? ' · ' : ''}
                                    {c ? <span style={{ color: '#1976d2' }}>{c}</span> : ''}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                {/* 담당 직원 정보 */}
                <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>👤 {companyName ? `${companyName} 문의` : '문의 담당자'}</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">담당자</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {JSON.parse(localStorage.getItem('user') || '{}').name || '관리자'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">연락처</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {formatPhone(JSON.parse(localStorage.getItem('user') || '{}').phone || '') || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="배차금액(기사 지급)"
                  value={broadcastData.broadcast_price ? parseInt(broadcastData.broadcast_price, 10).toLocaleString() : ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                    setBroadcastData({ ...broadcastData, broadcast_price: value ? parseInt(value, 10) : '' });
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">원</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff8e1' } }}
                  helperText={selectedOrder?.client_offered_price ? `고객 청구금액: ${parseInt(selectedOrder.client_offered_price).toLocaleString()}원` : '운전자에게 지급할 금액'}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>차량 종류 {broadcastData.required_vehicle_types.length === 0 && <Chip label="전체" size="small" sx={{ ml: 1 }} />}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {VEHICLE_TYPE_OPTIONS.map(type => (
                    <FormControlLabel key={type} control={
                      <Checkbox
                        checked={broadcastData.required_vehicle_types.includes(type)}
                        onChange={(e) => {
                          const types = e.target.checked
                            ? [...broadcastData.required_vehicle_types, type]
                            : broadcastData.required_vehicle_types.filter(t => t !== type);
                          setBroadcastData({ ...broadcastData, required_vehicle_types: types });
                        }}
                        size="small"
                      />
                    } label={type} sx={{ mr: 1, width: 130 }} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>적재중량 {broadcastData.required_weights.length === 0 && <Chip label="전체" size="small" sx={{ ml: 1 }} />}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {WEIGHT_OPTIONS_NEW.map(w => (
                    <FormControlLabel key={w} control={
                      <Checkbox
                        checked={broadcastData.required_weights.includes(w)}
                        onChange={(e) => {
                          const weights = e.target.checked
                            ? [...broadcastData.required_weights, w]
                            : broadcastData.required_weights.filter(v => v !== w);
                          setBroadcastData({ ...broadcastData, required_weights: weights });
                        }}
                        size="small"
                      />
                    } label={w} sx={{ mr: 1, width: 110 }} />
                  ))}
                </Box>
              </Grid>
              
              {/* 호출 대상 선택 */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>📢 호출 대상</Typography>
                  <RadioGroup
                    row
                    value={broadcastTargetType}
                    onChange={(e) => {
                      setBroadcastTargetType(e.target.value);
                      if (e.target.value === 'all') {
                        setSelectedDriverIds([]);
                      }
                    }}
                  >
                    <FormControlLabel value="all" control={<Radio />} label="전체 운전자" />
                    <FormControlLabel value="selected" control={<Radio />} label="특정 운전자 선택" />
                  </RadioGroup>

                  {broadcastTargetType === 'all' && (
                    <Box sx={{ mt: 1.5 }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>출발지 반경 (거리 필터)</InputLabel>
                        <Select
                          value={broadcastData.max_distance_km}
                          onChange={(e) => setBroadcastData({ ...broadcastData, max_distance_km: e.target.value })}
                          label="출발지 반경 (거리 필터)"
                        >
                          <MenuItem value="">제한 없음</MenuItem>
                          {[10, 20, 30, 50, 100].map((km) => (
                            <MenuItem key={km} value={km}>{km}km 이내</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {broadcastData.max_distance_km && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                          마지막 위치가 출발지 반경 {broadcastData.max_distance_km}km 이내인 기사에게만 호출됩니다.
                          위치 정보가 없는 기사는 제외됩니다.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {broadcastTargetType === 'selected' && (
                    <Autocomplete
                      multiple
                      options={allDrivers}
                      disableCloseOnSelect
                      getOptionLabel={(option) => `${option.name} (${option.vehicle_number || '-'}) - ${option.vehicle_type || '-'}`}
                      value={allDrivers.filter(d => selectedDriverIds.includes(d.driver_id))}
                      onChange={(event, newValue) => {
                        setSelectedDriverIds(newValue.map(d => d.driver_id));
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={<CheckBoxOutlineBlank fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {option.vehicle_number || '-'} | {option.vehicle_type || '-'} | ⭐ {Number(option.rating || 5).toFixed(1)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="운전자 선택" placeholder="이름 또는 차량번호로 검색" />
                      )}
                      sx={{ mt: 2 }}
                    />
                  )}
                  
                  {broadcastTargetType === 'selected' && selectedDriverIds.length > 0 && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      선택된 운전자: {selectedDriverIds.length}명
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialog(false)}>취소</Button>
          <Button variant="contained" color="warning" onClick={handleBroadcast}>
            호출 시작
          </Button>
        </DialogActions>
      </Dialog>

      {/* 신청자 목록 다이얼로그 */}
      <Dialog open={applicationsDialog} onClose={() => setApplicationsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          신청자 목록 ({applications.length}명)
        </DialogTitle>
        <DialogContent>
          {loadingApplications ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : applications.length === 0 ? (
            <Alert severity="info">아직 신청한 운전자가 없습니다.</Alert>
          ) : (
            <List>
              {applications.map((app, index) => (
                <React.Fragment key={app.application_id}>
                  <ListItem
                    secondaryAction={
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApproveApplication(app.application_id)}
                      >
                        배차
                      </Button>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#1976d2' }}>
                        <LocalShipping />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight={600}>{app.driver_name}</Typography>
                          <Chip
                            icon={<Star sx={{ fontSize: 14 }} />}
                            label={Number(app.rating || 5).toFixed(1)}
                            size="small"
                            color="warning"
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2">{app.vehicle_number} ({app.vehicle_type})</Typography>
                          <Typography variant="body2" color="textSecondary">{formatPhone(app.driver_phone || '')}</Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < applications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationsDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 상세 보기 다이얼로그 */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>주문 상세</DialogTitle>
        <DialogContent>
          {orderDetail && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">주문번호</Typography>
                <Typography fontWeight={600}>{orderDetail.order_number}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">금액상태</Typography>
                {getPriceStatusChip(orderDetail.price_status)}
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">배차상태</Typography>
                {getDispatchStatusChip(orderDetail.status)}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              {/* 구분 */}
              {parseJsonArr(orderDetail.dispatch_types).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>구분</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {parseJsonArr(orderDetail.dispatch_types).map((t) => (
                      <Chip key={t} label={DISPATCH_TYPE_LABEL[t] || t} size="small" color={DISPATCH_TYPE_COLOR[t] || 'default'} />
                    ))}
                  </Box>
                </Grid>
              )}
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">상차지</Typography>
                <Typography>{orderDetail.origin_address}</Typography>
                <Typography variant="body2">
                  {orderDetail.origin_contact_name ? `${orderDetail.origin_contact_name} · ` : ''}{orderDetail.origin_contact || '-'}
                </Typography>
                {parseJsonArr(orderDetail.loading_methods).length > 0 && (
                  <Typography variant="caption" color="primary">상차: {parseJsonArr(orderDetail.loading_methods).join(', ')}</Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">하차지</Typography>
                <Typography>{orderDetail.destination_address}</Typography>
                <Typography variant="body2">
                  {orderDetail.destination_contact_name ? `${orderDetail.destination_contact_name} · ` : ''}{orderDetail.destination_contact || '-'}
                </Typography>
                {parseJsonArr(orderDetail.unloading_methods).length > 0 && (
                  <Typography variant="caption" color="primary">하차: {parseJsonArr(orderDetail.unloading_methods).join(', ')}</Typography>
                )}
              </Grid>
              {/* 경유지 */}
              {parseJsonArr(orderDetail.waypoints).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">경유지</Typography>
                  {parseJsonArr(orderDetail.waypoints).map((w, i) => {
                    const addr = typeof w === 'string' ? w : w?.address;
                    const cn = typeof w === 'object' ? w?.contact_name : '';
                    const c = typeof w === 'object' ? w?.contact : '';
                    return (
                      <Typography key={i} variant="body2">
                        • {i + 1}. {addr}
                        {(cn || c) && <span style={{ color: '#666', fontSize: '0.85em' }}> ({cn}{cn && c ? ' · ' : ''}{c})</span>}
                      </Typography>
                    );
                  })}
                </Grid>
              )}
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">📅 상차일시</Typography>
                  <Typography fontWeight={600} color="#2e7d32">
                    {fmtDateTime(orderDetail.pickup_time || orderDetail.pickup_datetime) || '미지정'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">📅 하차일시</Typography>
                  <Typography fontWeight={600} color="#FF9800">
                    {fmtDateTime(orderDetail.desired_delivery_datetime) || '미지정'}
                  </Typography>
                </Box>
              </Grid>
              {/* 차종 / 톤수 */}
              {(parseJsonArr(orderDetail.vehicle_types).length > 0 || orderDetail.required_vehicle_type
                || parseJsonArr(orderDetail.vehicle_weights).length > 0 || orderDetail.required_weight) && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">차종</Typography>
                    {(parseJsonArr(orderDetail.vehicle_types).length
                      ? parseJsonArr(orderDetail.vehicle_types)
                      : (orderDetail.required_vehicle_type ? [orderDetail.required_vehicle_type] : [])
                    ).map((v) => <Chip key={v} label={v} size="small" color="primary" />)}
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>톤수</Typography>
                    {(parseJsonArr(orderDetail.vehicle_weights).length
                      ? parseJsonArr(orderDetail.vehicle_weights)
                      : (orderDetail.required_weight ? [orderDetail.required_weight + 't'] : [])
                    ).map((w) => <Chip key={w} label={w} size="small" color="secondary" />)}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary" sx={{ color: '#e65100' }}>제시금액(고객)</Typography>
                <Typography fontWeight={700} sx={{ color: '#e65100' }}>
                  {formatPrice(orderDetail.client_offered_price)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary" sx={{ color: '#1976d2' }}>배차금액(기사)</Typography>
                <Typography fontWeight={700} sx={{ color: '#1976d2' }}>
                  {formatPrice(orderDetail.confirmed_price)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">호출금액</Typography>
                <Typography>{formatPrice(orderDetail.broadcast_price)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="textSecondary">예상 수익</Typography>
                {(() => {
                  const customerPrice = Number(orderDetail.client_offered_price) || 0;
                  const driverPrice = Number(orderDetail.confirmed_price || orderDetail.broadcast_price) || 0;
                  if (!customerPrice || !driverPrice) return <Typography>-</Typography>;
                  const profit = customerPrice - driverPrice;
                  return (
                    <Typography fontWeight={700} sx={{ color: profit >= 0 ? '#2e7d32' : '#d32f2f' }}>
                      {profit >= 0 ? '+' : ''}{profit.toLocaleString()}원
                    </Typography>
                  );
                })()}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">업체</Typography>
                <Typography>{orderDetail.company_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">운전자</Typography>
                <Typography>{orderDetail.driver_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">등록자</Typography>
                <Typography>{orderDetail.created_by_name || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">배차자</Typography>
                <Typography>{orderDetail.assigned_by_name || '-'}</Typography>
              </Grid>
              {orderDetail.special_notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">특이사항</Typography>
                  <Typography>{orderDetail.special_notes}</Typography>
                </Grid>
              )}
              {(orderDetail.received_by_name || orderDetail.received_signature) && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: 1 }}>
                    <Typography variant="body2" color="success.dark" fontWeight={700} sx={{ mb: 1 }}>
                      ✅ 수령 확인
                    </Typography>
                    {orderDetail.received_by_name && (
                      <Typography variant="body2">담당자: <strong>{orderDetail.received_by_name}</strong></Typography>
                    )}
                    {orderDetail.received_signature && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">서명</Typography>
                        <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fff', border: '1px solid #ccc', borderRadius: 1, display: 'inline-block' }}>
                          <img
                            src={`${(process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '')}${orderDetail.received_signature}`}
                            alt="수령자 서명"
                            style={{ maxWidth: 300, maxHeight: 150 }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="textSecondary">기사 송금일</Typography>
                <Typography fontWeight={600} color={orderDetail.payment_date ? 'success.main' : 'text.disabled'}>
                  {orderDetail.payment_date ? orderDetail.payment_date.slice(0, 16).replace('T', ' ') : '-'}
                </Typography>
              </Grid>
              {orderDetail.remarks && (
                <Grid item xs={8}>
                  <Typography variant="body2" color="textSecondary">비고</Typography>
                  <Typography>{orderDetail.remarks}</Typography>
                </Grid>
              )}
              {orderDetail.photos && orderDetail.photos.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    화물 사진 ({orderDetail.photos.length}장)
                  </Typography>
                  <Grid container spacing={1}>
                    {orderDetail.photos.map((photo, idx) => (
                      <Grid item xs={6} sm={4} key={photo.photo_id}>
                        <Box
                          sx={{ position: 'relative', cursor: 'pointer', borderRadius: 1, overflow: 'hidden', border: '1px solid #ddd' }}
                          onClick={() => { setPhotoViewerPhotos(orderDetail.photos); setPhotoViewerIndex(idx); setPhotoViewerDialog(true); }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${photo.file_path}`}
                            alt={photo.original_name || `사진 ${idx + 1}`}
                            style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.4)', px: 1, py: 0.5 }}>
                            <Typography variant="caption" color="white" noWrap>{photo.original_name || `사진 ${idx + 1}`}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Chat />} onClick={() => { setDetailDialog(false); openMessageDialog(orderDetail); }}>
            메시지
          </Button>
          <Button onClick={() => setDetailDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 주문 수정 다이얼로그 */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>주문 수정</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>출발지 정보</Typography>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="출발지 주소"
                value={editData.origin_address}
                onChange={(e) => setEditData({ ...editData, origin_address: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" variant="outlined" startIcon={<Search />}
                        onClick={() => {
                          if (!window.daum?.Postcode) return;
                          new window.daum.Postcode({
                            oncomplete: (data) => setEditData((prev) => ({ ...prev, origin_address: data.roadAddress || data.jibunAddress })),
                          }).open();
                        }}
                        sx={{ whiteSpace: 'nowrap' }}>검색</Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="상차지 담당자"
                value={editData.origin_contact_name}
                onChange={(e) => setEditData({ ...editData, origin_contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="상차지 연락처"
                value={editData.origin_contact}
                onChange={(e) => setEditData({ ...editData, origin_contact: formatPhone(e.target.value) })}
                placeholder="010-1234-5678"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="상차일시"
                type="datetime-local"
                value={editData.pickup_datetime}
                onChange={(e) => setEditData({ ...editData, pickup_datetime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#e8f5e9' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>도착지 정보</Typography>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="도착지 주소"
                value={editData.destination_address}
                onChange={(e) => setEditData({ ...editData, destination_address: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" variant="outlined" startIcon={<Search />}
                        onClick={() => {
                          if (!window.daum?.Postcode) return;
                          new window.daum.Postcode({
                            oncomplete: (data) => setEditData((prev) => ({ ...prev, destination_address: data.roadAddress || data.jibunAddress })),
                          }).open();
                        }}
                        sx={{ whiteSpace: 'nowrap' }}>검색</Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="하차지 담당자"
                value={editData.destination_contact_name}
                onChange={(e) => setEditData({ ...editData, destination_contact_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="하차지 연락처"
                value={editData.destination_contact}
                onChange={(e) => setEditData({ ...editData, destination_contact: formatPhone(e.target.value) })}
                placeholder="010-9876-5432"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="하차일시"
                type="datetime-local"
                value={editData.desired_delivery_datetime}
                onChange={(e) => setEditData({ ...editData, desired_delivery_datetime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff3e0' } }}
              />
            </Grid>
            {/* 구분 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>구분 / 상하차 방법</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ width: 60 }}>구분</Typography>
                {DISPATCH_TYPES.map((d) => (
                  <FormControlLabel key={d.key} sx={{ mr: 1 }}
                    control={<Checkbox size="small" checked={editData.dispatch_types.includes(d.key)}
                      onChange={() => setEditData((p) => ({ ...p, dispatch_types: p.dispatch_types.includes(d.key) ? p.dispatch_types.filter(x => x !== d.key) : [...p.dispatch_types, d.key] }))} />}
                    label={d.label} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" sx={{ width: 60 }}>상차방법</Typography>
                {HANDLING_METHODS.map((m) => (
                  <FormControlLabel key={'l' + m} sx={{ mr: 1 }}
                    control={<Checkbox size="small" checked={editData.loading_methods.includes(m)}
                      onChange={() => setEditData((p) => ({ ...p, loading_methods: p.loading_methods.includes(m) ? p.loading_methods.filter(x => x !== m) : [...p.loading_methods, m] }))} />}
                    label={m} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" sx={{ width: 60 }}>하차방법</Typography>
                {HANDLING_METHODS.map((m) => (
                  <FormControlLabel key={'u' + m} sx={{ mr: 1 }}
                    control={<Checkbox size="small" checked={editData.unloading_methods.includes(m)}
                      onChange={() => setEditData((p) => ({ ...p, unloading_methods: p.unloading_methods.includes(m) ? p.unloading_methods.filter(x => x !== m) : [...p.unloading_methods, m] }))} />}
                    label={m} />
                ))}
              </Box>
            </Grid>
            {/* 경유지 (구분에 '경유' 있거나 기존 경유지가 있을 때 표시) */}
            {(editData.dispatch_types.includes('waypoint') || (editData.waypoints && editData.waypoints.length > 0)) && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="primary">🚏 경유지</Typography>
                  <Button size="small" startIcon={<Add />}
                    onClick={() => setEditData((p) => ({ ...p, waypoints: [...(p.waypoints || []), { address: '' }] }))}>
                    경유지 추가
                  </Button>
                </Box>
                {(editData.waypoints || []).map((wp, i) => (
                  <Box key={i} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="secondary">경유지 {i + 1}</Typography>
                      <IconButton size="small" color="error"
                        onClick={() => setEditData({ ...editData, waypoints: editData.waypoints.filter((_, idx) => idx !== i) })}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField fullWidth size="small" label="주소" value={wp.address || ''}
                          onChange={(e) => {
                            const next = [...editData.waypoints];
                            next[i] = { ...next[i], address: e.target.value };
                            setEditData({ ...editData, waypoints: next });
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button size="small" variant="outlined" startIcon={<Search />}
                                  onClick={() => {
                                    if (!window.daum?.Postcode) return;
                                    new window.daum.Postcode({
                                      oncomplete: (data) => {
                                        const addr = data.roadAddress || data.jibunAddress;
                                        const next = [...editData.waypoints];
                                        next[i] = { ...next[i], address: addr };
                                        setEditData({ ...editData, waypoints: next });
                                      },
                                    }).open();
                                  }}>검색</Button>
                              </InputAdornment>
                            ),
                          }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="담당자" value={wp.contact_name || ''}
                          onChange={(e) => {
                            const next = [...editData.waypoints];
                            next[i] = { ...next[i], contact_name: e.target.value };
                            setEditData({ ...editData, waypoints: next });
                          }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="연락처" value={wp.contact || ''}
                          onChange={(e) => {
                            const next = [...editData.waypoints];
                            next[i] = { ...next[i], contact: formatPhone(e.target.value) };
                            setEditData({ ...editData, waypoints: next });
                          }}
                          placeholder="010-1234-5678" />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                {(!editData.waypoints || editData.waypoints.length === 0) && (
                  <Typography variant="caption" color="text.secondary">경유지가 없습니다. [경유지 추가] 버튼을 누르세요.</Typography>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>금액 정보</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="제시금액(고객)"
                value={editData.client_offered_price ? parseInt(editData.client_offered_price, 10).toLocaleString() : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                  setEditData({ ...editData, client_offered_price: value ? parseInt(value, 10) : '' });
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                }}
                helperText="고객에게 제시하는 금액"
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff3e0' } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="배차금액(기사)"
                value={editData.confirmed_price ? parseInt(editData.confirmed_price, 10).toLocaleString() : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                  setEditData({ ...editData, confirmed_price: value ? parseInt(value, 10) : '' });
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                }}
                helperText="기사에게 지급하는 금액"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>기타</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple options={VEHICLE_TYPE_OPTIONS}
                value={editData.vehicle_types}
                onChange={(_, v) => { if (v.length <= 3) setEditData({ ...editData, vehicle_types: v, required_vehicle_type: v[0] || '' }); }}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => <Chip key={opt} label={opt} color="primary" size="small" {...getTagProps({ index: idx })} />)
                }
                renderInput={(params) => <TextField {...params} label="차종 (최대 3개)" placeholder="차종 선택" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple options={WEIGHT_OPTIONS_NEW}
                value={editData.vehicle_weights}
                onChange={(_, v) => { if (v.length <= 3) setEditData({ ...editData, vehicle_weights: v, required_weight: v[0] ? String(v[0]).replace(/[^\d.]/g, '') : '' }); }}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => <Chip key={opt} label={opt} color="secondary" size="small" {...getTagProps({ index: idx })} />)
                }
                renderInput={(params) => <TextField {...params} label="톤수 (최대 3개)" placeholder="톤수 선택" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="특이사항"
                value={editData.special_notes}
                onChange={(e) => setEditData({ ...editData, special_notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>입금 / 비고</Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="기사 송금일"
                type="datetime-local"
                value={editData.payment_date}
                onChange={(e) => setEditData({ ...editData, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="운전기사에게 배차금액을 송금 처리한 날짜를 입력하세요. (운전자앱 거래기록의 '입금일'로 표시됩니다)"
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="비고"
                value={editData.remarks}
                onChange={(e) => setEditData({ ...editData, remarks: e.target.value })}
                placeholder="메모, 특이사항 등"
              />
            </Grid>
            {editPhotos.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    화물 사진 ({editPhotos.length}장)
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    {editPhotos.map((photo, idx) => (
                      <Grid item xs={4} sm={3} key={photo.photo_id}>
                        <Box
                          sx={{ position: 'relative', cursor: 'pointer', borderRadius: 1, overflow: 'hidden', border: '1px solid #ddd' }}
                          onClick={() => { setPhotoViewerPhotos(editPhotos); setPhotoViewerIndex(idx); setPhotoViewerDialog(true); }}
                        >
                          <img
                            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${photo.file_path}`}
                            alt={photo.original_name || `사진 ${idx + 1}`}
                            style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                          />
                          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.4)', px: 0.5, py: 0.3 }}>
                            <Typography variant="caption" color="white" noWrap sx={{ fontSize: '0.65rem' }}>{photo.original_name || `사진 ${idx + 1}`}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>취소</Button>
          <Button variant="contained" onClick={handleEditSave}>저장</Button>
        </DialogActions>
      </Dialog>

      {/* 금액 제시 다이얼로그 */}
      <Dialog open={proposePriceDialog} onClose={() => setProposePriceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💰 배차 금액 제시</DialogTitle>
        <DialogContent>
          {proposePriceOrder && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                주문번호: <strong>{proposePriceOrder.order_number}</strong><br />
                고객: {proposePriceOrder.company_name || proposePriceOrder.contact_person}<br />
                {proposePriceOrder.client_offered_price && `고객 제시 금액: ${parseInt(proposePriceOrder.client_offered_price).toLocaleString()}원`}
              </Typography>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>상차</strong> {proposePriceOrder.origin_address}
                  {(proposePriceOrder.origin_contact_name || proposePriceOrder.origin_contact) && (
                    <span style={{ color: '#666' }}> · {proposePriceOrder.origin_contact_name}{proposePriceOrder.origin_contact_name && proposePriceOrder.origin_contact ? ' ' : ''}{proposePriceOrder.origin_contact}</span>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  <strong>하차</strong> {proposePriceOrder.destination_address}
                  {(proposePriceOrder.destination_contact_name || proposePriceOrder.destination_contact) && (
                    <span style={{ color: '#666' }}> · {proposePriceOrder.destination_contact_name}{proposePriceOrder.destination_contact_name && proposePriceOrder.destination_contact ? ' ' : ''}{proposePriceOrder.destination_contact}</span>
                  )}
                </Typography>
                {parseJsonArr(proposePriceOrder.waypoints).length > 0 && (
                  <Box sx={{ mt: 0.5, p: 0.8, bgcolor: '#f3e5f5', borderLeft: '3px solid #9c27b0', borderRadius: 0.5 }}>
                    <Typography variant="caption" color="secondary" fontWeight={700}>🚏 경유지 ({parseJsonArr(proposePriceOrder.waypoints).length})</Typography>
                    {parseJsonArr(proposePriceOrder.waypoints).map((w, i) => {
                      const addr = typeof w === 'string' ? w : w?.address;
                      const cn = typeof w === 'object' ? w?.contact_name : '';
                      const c = typeof w === 'object' ? w?.contact : '';
                      return (
                        <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                          {i + 1}. {addr}{(cn || c) && <span style={{ color: '#666' }}> · {cn}{cn && c ? ' ' : ''}{c}</span>}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {parseJsonArr(proposePriceOrder.dispatch_types).map((t) => (
                    <Chip key={t} label={DISPATCH_TYPE_LABEL[t] || t} size="small" color={DISPATCH_TYPE_COLOR[t] || 'default'} />
                  ))}
                  {(parseJsonArr(proposePriceOrder.vehicle_types).length
                    ? parseJsonArr(proposePriceOrder.vehicle_types)
                    : (proposePriceOrder.required_vehicle_type ? [proposePriceOrder.required_vehicle_type] : [])
                  ).map((v) => <Chip key={v} label={v} size="small" color="primary" variant="outlined" />)}
                  {(parseJsonArr(proposePriceOrder.vehicle_weights).length
                    ? parseJsonArr(proposePriceOrder.vehicle_weights)
                    : (proposePriceOrder.required_weight ? [proposePriceOrder.required_weight + 't'] : [])
                  ).map((w) => <Chip key={w} label={w} size="small" color="secondary" variant="outlined" />)}
                </Box>
                {(proposePriceOrder.pickup_time || proposePriceOrder.pickup_datetime || proposePriceOrder.desired_delivery_datetime) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    상차 {fmtDateTime(proposePriceOrder.pickup_time || proposePriceOrder.pickup_datetime) || '-'} / 하차 {fmtDateTime(proposePriceOrder.desired_delivery_datetime) || '-'}
                  </Typography>
                )}
              </Box>
              <TextField
                fullWidth
                label="고객에게 제시할 금액 (원)"
                type="number"
                value={proposePriceValue}
                onChange={(e) => setProposePriceValue(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                autoFocus
              />
              {(() => {
                const driverPrice = parseInt(proposePriceOrder.confirmed_price || proposePriceOrder.broadcast_price || 0, 10);
                if (!driverPrice) return null;
                const profit = proposePriceValue ? Number(proposePriceValue) - driverPrice : null;
                return (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    배차금액(기사): <strong>{driverPrice.toLocaleString()}원</strong>
                    {profit !== null && (
                      <span style={{ color: profit >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 700 }}>
                        {' '}· 예상 수익 {profit >= 0 ? '+' : ''}{profit.toLocaleString()}원
                      </span>
                    )}
                  </Typography>
                );
              })()}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                금액 제시 시 고객 앱에 푸시 알림과 입금 안내가 전송됩니다.
              </Typography>

              {/* 최소경비 산출 */}
              <Box sx={{ mt: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>🧮 예상 최소경비 산출</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleEstimateCost}
                    disabled={costEstimating}
                    startIcon={costEstimating ? <CircularProgress size={14} /> : null}
                  >
                    {costEstimate ? '다시 계산' : '계산하기'}
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  주소·차종({proposePriceOrder.required_vehicle_type || '미지정'}) 기반 추정치 — 직선/운행거리, 주유비, 톨비, 인건비, 감가상각비 포함 (왕복 기준)
                </Typography>

                {costEstimateError && (
                  <Alert severity="error" sx={{ mt: 1 }}>{costEstimateError}</Alert>
                )}

                {costEstimate && (
                  <Box sx={{ mt: 1.5 }}>
                    <Grid container spacing={0.5}>
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">차량 (가정)</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption">{costEstimate.vehicleLabel}</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">직선 거리</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption">{costEstimate.straightKm} km</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">운행 거리 (편도 / 왕복)</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption">{costEstimate.onewayKm} km / {costEstimate.totalKm} km</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">예상 시간 (운전 + 상하차)</Typography></Grid>
                      <Grid item xs={6}><Typography variant="caption">{costEstimate.totalHours} 시간</Typography></Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={0.5}>
                      <Grid item xs={7}><Typography variant="body2">⛽ 주유비 ({costEstimate.fuelLiters} L)</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.fuelCost.toLocaleString()}원</Typography></Grid>
                      <Grid item xs={7}><Typography variant="body2">🛣️ 톨비 (고속 {costEstimate.tollKm} km)</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.tollCost.toLocaleString()}원</Typography></Grid>
                      <Grid item xs={7}><Typography variant="body2">👤 인건비</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.laborCost.toLocaleString()}원</Typography></Grid>
                      <Grid item xs={7}><Typography variant="body2">🚛 차량 감가/유지비</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2">{costEstimate.depreciationCost.toLocaleString()}원</Typography></Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={0.5}>
                      <Grid item xs={7}><Typography variant="body2" color="text.secondary">소계</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2" color="text.secondary">{costEstimate.subtotal.toLocaleString()}원</Typography></Grid>
                      <Grid item xs={7}><Typography variant="body2" color="text.secondary">마진 ({Math.round(costEstimate.marginRate * 100)}%)</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}><Typography variant="body2" color="text.secondary">{costEstimate.margin.toLocaleString()}원</Typography></Grid>
                      <Grid item xs={7}><Typography variant="subtitle2" fontWeight={700} color="primary">권장 최소금액</Typography></Grid>
                      <Grid item xs={5} sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="primary">{costEstimate.recommendedPrice.toLocaleString()}원</Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                      <Button size="small" variant="contained" color="primary" onClick={applyEstimatedPrice}>
                        이 금액으로 입력
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProposePriceDialog(false)}>취소</Button>
          <Button variant="outlined" color="info" onClick={handleNotifyPrice} disabled={proposePriceLoading}>
            금액 알림 (결재대기 직행)
          </Button>
          <Button variant="contained" color="warning" onClick={handleProposePrice} disabled={proposePriceLoading}>
            {proposePriceLoading ? <CircularProgress size={20} /> : '금액 제시 및 알림 전송'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 사진 뷰어 (라이트박스) */}
      <Dialog open={photoViewerDialog} onClose={() => setPhotoViewerDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={600}>화물 사진 ({photoViewerIndex + 1} / {photoViewerPhotos.length})</Typography>
          <IconButton onClick={() => setPhotoViewerDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000', position: 'relative', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {photoViewerPhotos.length > 0 && (
            <>
              <img
                src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${photoViewerPhotos[photoViewerIndex]?.file_path}`}
                alt={photoViewerPhotos[photoViewerIndex]?.original_name || ''}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
              {photoViewerPhotos.length > 1 && (
                <>
                  <IconButton
                    onClick={() => setPhotoViewerIndex((i) => (i - 1 + photoViewerPhotos.length) % photoViewerPhotos.length)}
                    sx={{ position: 'absolute', left: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    onClick={() => setPhotoViewerIndex((i) => (i + 1) % photoViewerPhotos.length)}
                    sx={{ position: 'absolute', right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <ArrowForward />
                  </IconButton>
                </>
              )}
            </>
          )}
        </DialogContent>
        {photoViewerPhotos.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, p: 1, bgcolor: '#111', overflowX: 'auto' }}>
            {photoViewerPhotos.map((photo, idx) => (
              <Box
                key={photo.photo_id}
                onClick={() => setPhotoViewerIndex(idx)}
                sx={{ flexShrink: 0, width: 60, height: 60, cursor: 'pointer', borderRadius: 1, overflow: 'hidden', border: idx === photoViewerIndex ? '2px solid #1976d2' : '2px solid transparent' }}
              >
                <img
                  src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${photo.file_path}`}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Dialog>

      {/* 메시지 다이얼로그 */}
      <DriverLocationDialog
        open={locationDialog}
        onClose={() => setLocationDialog(false)}
        order={locationOrder}
      />

      <Dialog open={messageDialog} onClose={closeMessageDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={600}>💬 메시지 {messageOrder && `- ${messageOrder.order_number}`}</Typography>
          <IconButton onClick={closeMessageDialog}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 400, overflowY: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
            {messages.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ mt: 10 }}>메시지가 없습니다.</Typography>
            ) : (
              messages.map((msg) => (
                <Box key={msg.message_id} sx={{
                  display: 'flex', justifyContent: msg.sender_type === 'staff' ? 'flex-end' : 'flex-start', mb: 1
                }}>
                  <Box sx={{
                    maxWidth: '75%', p: 1.5, borderRadius: 2,
                    bgcolor: msg.sender_type === 'staff' ? '#1976d2' : '#fff',
                    color: msg.sender_type === 'staff' ? '#fff' : '#333',
                    boxShadow: 1,
                  }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.3 }}>
                      {msg.sender_name} · {new Date(msg.created_at).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.message}</Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', p: 1.5, gap: 1, borderTop: '1px solid #e0e0e0' }}>
          <TextField
            fullWidth size="small" placeholder="메시지를 입력하세요..."
            value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
          />
          <IconButton color="primary" onClick={handleSendMessage} disabled={messageLoading || !newMessage.trim()}>
            <Send />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
}

export default OrderList;
